/**
 * Service de planification complexe multi-intervenants avec alternance semaine A/B
 *
 * Ce service gère :
 * - La génération de plannings avec jours fixes et alternance A/B
 * - La détection automatique des majorations (dimanche, jours fériés, 1er mai)
 * - Le calcul du nombre d'interventions par type de majoration
 * - La conversion du planning en lignes de devis
 * - La validation de la cohérence du planning
 */

import { MAJORATIONS } from './tarificationService';
import {
  getFrenchHolidays,
  isFrenchHoliday,
  isMay1st,
  getHolidaysInRange
} from '../utils/frenchHolidays';
import {
  getWeekType,
  getDatesForWeekday,
  countWeekTypes
} from '../utils/weekCalculations';

/**
 * Arrondit les heures à un nombre de décimales donné pour éviter les erreurs de précision
 * en virgule flottante (ex: 30.000000000000018 → 30.0)
 *
 * @param {number} hours - Nombre d'heures à arrondir
 * @param {number} decimals - Nombre de décimales (défaut: 2)
 * @returns {number} Heures arrondies
 */
function roundHours(hours, decimals = 2) {
  const multiplier = Math.pow(10, decimals);
  return Math.round(hours * multiplier) / multiplier;
}

/**
 * Détermine le type de majoration pour une date donnée
 * Ordre de priorité : 1er mai > Jours fériés > Dimanche
 *
 * @param {Date} date - Date à analyser
 * @param {Array<Date>} holidays - Liste des jours fériés de la période
 * @returns {string} 'none' | 'weekend' | 'holiday' | 'may1'
 */
export function getMajorationTypeForDate(date, holidays) {
  // Priorité 1 : Vérifier si c'est le 1er mai (+100%)
  if (isMay1st(date)) {
    return 'may1';
  }

  // Priorité 2 : Vérifier si c'est un jour férié (+50%)
  if (isFrenchHoliday(date, holidays)) {
    return 'holiday';
  }

  // Priorité 3 : Vérifier si c'est un dimanche (+25%)
  if (date.getDay() === 0) {
    return 'weekend';
  }

  return 'none';
}

/**
 * Génère toutes les dates d'intervention pour un employé selon sa configuration
 *
 * @param {Object} employeeSchedule - Configuration de l'employé
 * @param {Date} startDate - Date de début de période
 * @param {Date} endDate - Date de fin de période
 * @param {Array<Date>} holidays - Jours fériés de la période
 * @returns {Array<Object>} Liste des interventions { date, day, hours, majoration }
 */
function generateEmployeeInterventions(employeeSchedule, startDate, endDate, holidays) {
  const interventions = [];

  // Générer les interventions pour les jours fixes (chaque semaine)
  if (employeeSchedule.fixedDays && employeeSchedule.fixedDays.length > 0) {
    employeeSchedule.fixedDays.forEach((dayConfig) => {
      // Support ancien format (hours) et nouveau format (totalHours/totalMinutes/passagesPerDay)
      const day = dayConfig.day;
      const totalHours = dayConfig.totalHours !== undefined ? dayConfig.totalHours : (dayConfig.hours || 3);
      const totalMinutes = dayConfig.totalMinutes || 0;
      const passagesPerDay = dayConfig.passagesPerDay || 1;

      // Calculer la durée par passage en heures décimales
      const totalTimeHours = totalHours + (totalMinutes / 60);
      const hoursPerPassage = roundHours(totalTimeHours / passagesPerDay);

      const dates = getDatesForWeekday(startDate, endDate, day);
      dates.forEach(date => {
        const majoration = getMajorationTypeForDate(date, holidays);

        // Créer N interventions pour cette date (N = passagesPerDay)
        for (let passageIndex = 0; passageIndex < passagesPerDay; passageIndex++) {
          interventions.push({
            date: new Date(date),
            day,
            hours: hoursPerPassage,
            majoration,
            weekType: 'fixed', // Jour fixe
            passageNumber: passageIndex + 1,
            totalPassages: passagesPerDay
          });
        }
      });
    });
  }

  // Générer les interventions pour l'alternance semaine A/B
  if (employeeSchedule.alternatingPattern && employeeSchedule.alternatingPattern.enabled) {
    const { weekA, weekB } = employeeSchedule.alternatingPattern;

    // Semaine A (impaire)
    if (weekA && weekA.length > 0) {
      weekA.forEach((dayConfig) => {
        // Support ancien format (hours) et nouveau format (totalHours/totalMinutes/passagesPerDay)
        const day = dayConfig.day;
        const totalHours = dayConfig.totalHours !== undefined ? dayConfig.totalHours : (dayConfig.hours || 3);
        const totalMinutes = dayConfig.totalMinutes || 0;
        const passagesPerDay = dayConfig.passagesPerDay || 1;

        // Calculer la durée par passage en heures décimales
        const totalTimeHours = totalHours + (totalMinutes / 60);
        const hoursPerPassage = roundHours(totalTimeHours / passagesPerDay);

        const dates = getDatesForWeekday(startDate, endDate, day, startDate, 'A');
        dates.forEach(date => {
          const majoration = getMajorationTypeForDate(date, holidays);

          // Créer N interventions pour cette date (N = passagesPerDay)
          for (let passageIndex = 0; passageIndex < passagesPerDay; passageIndex++) {
            interventions.push({
              date: new Date(date),
              day,
              hours: hoursPerPassage,
              majoration,
              weekType: 'A',
              passageNumber: passageIndex + 1,
              totalPassages: passagesPerDay
            });
          }
        });
      });
    }

    // Semaine B (paire)
    if (weekB && weekB.length > 0) {
      weekB.forEach((dayConfig) => {
        // Support ancien format (hours) et nouveau format (totalHours/totalMinutes/passagesPerDay)
        const day = dayConfig.day;
        const totalHours = dayConfig.totalHours !== undefined ? dayConfig.totalHours : (dayConfig.hours || 3);
        const totalMinutes = dayConfig.totalMinutes || 0;
        const passagesPerDay = dayConfig.passagesPerDay || 1;

        // Calculer la durée par passage en heures décimales
        const totalTimeHours = totalHours + (totalMinutes / 60);
        const hoursPerPassage = roundHours(totalTimeHours / passagesPerDay);

        const dates = getDatesForWeekday(startDate, endDate, day, startDate, 'B');
        dates.forEach(date => {
          const majoration = getMajorationTypeForDate(date, holidays);

          // Créer N interventions pour cette date (N = passagesPerDay)
          for (let passageIndex = 0; passageIndex < passagesPerDay; passageIndex++) {
            interventions.push({
              date: new Date(date),
              day,
              hours: hoursPerPassage,
              majoration,
              weekType: 'B',
              passageNumber: passageIndex + 1,
              totalPassages: passagesPerDay
            });
          }
        });
      });
    }
  }

  return interventions;
}

/**
 * Génère le planning complet pour tous les employés sur une période
 *
 * @param {Date} dateDebut - Date de début de période
 * @param {Date} dateFin - Date de fin de période
 * @param {Array<Object>} employeeSchedules - Configurations des employés
 * @returns {Object} Planning complet { employeeId: [...interventions] }
 */
export function generateInterventionSchedule(dateDebut, dateFin, employeeSchedules) {
  // Validation des dates
  if (!dateDebut || !dateFin) {
    throw new Error('Les dates de début et de fin sont obligatoires');
  }

  if (dateDebut >= dateFin) {
    throw new Error('La date de début doit être antérieure à la date de fin');
  }

  // Calculer tous les jours fériés de la période
  const holidays = getHolidaysInRange(dateDebut, dateFin);

  console.log(`📅 Génération du planning du ${dateDebut.toLocaleDateString('fr-FR')} au ${dateFin.toLocaleDateString('fr-FR')}`);
  console.log(`🎊 Jours fériés détectés : ${holidays.length}`);

  const schedule = {};

  employeeSchedules.forEach(empSchedule => {
    // Ignorer les employés sans ID
    if (!empSchedule.employeeId) {
      console.warn('⚠️ Employé ignoré (pas d\'ID):', empSchedule);
      return;
    }

    const interventions = generateEmployeeInterventions(
      empSchedule,
      dateDebut,
      dateFin,
      holidays
    );

    schedule[empSchedule.employeeId] = {
      employeeId: empSchedule.employeeId,
      employeeName: empSchedule.employeeName || 'Intervenant',
      interventions,
      distanceAllerKm: empSchedule.distanceAllerKm || 0
    };

    console.log(`✅ ${empSchedule.employeeName || empSchedule.employeeId} : ${interventions.length} interventions générées`);
  });

  return schedule;
}

/**
 * Calcule le nombre total d'interventions par employé et par type de majoration
 *
 * @param {Object} schedule - Planning généré par generateInterventionSchedule
 * @returns {Object} Statistiques par employé et par type
 */
export function calculateTotalInterventions(schedule) {
  console.log('📊 calculateTotalInterventions - schedule reçu:', schedule);
  const result = {};

  Object.entries(schedule).forEach(([employeeId, data]) => {
    // Vérifier que data et data.interventions existent
    if (!data) {
      console.warn(`⚠️ Données nulles pour l'employé ${employeeId}`);
      return;
    }

    if (!data.interventions) {
      console.warn(`⚠️ Propriété interventions manquante pour l'employé ${employeeId}:`, data);
      return;
    }

    if (!Array.isArray(data.interventions)) {
      console.warn(`⚠️ interventions n'est pas un tableau pour l'employé ${employeeId}:`, data.interventions);
      return;
    }

    const stats = {
      employeeId,
      employeeName: data.employeeName,
      distanceAllerKm: data.distanceAllerKm,

      // Interventions normales (aucune majoration)
      none: {
        interventions: 0,
        hours: 0,
        dates: []
      },

      // Interventions dimanche (+25%)
      weekend: {
        interventions: 0,
        hours: 0,
        dates: []
      },

      // Interventions jours fériés (+50%)
      holiday: {
        interventions: 0,
        hours: 0,
        dates: []
      },

      // Interventions 1er mai (+100%)
      may1: {
        interventions: 0,
        hours: 0,
        dates: []
      },

      // Total
      total: {
        interventions: 0,
        hours: 0
      }
    };

    // Grouper par type de majoration
    data.interventions.forEach(intervention => {
      const type = intervention.majoration;

      // Vérifier que le type de majoration est valide
      if (!stats[type]) {
        console.warn(`⚠️ Type de majoration inconnu: "${type}", intervention ignorée:`, intervention);
        return;
      }

      stats[type].interventions++;
      stats[type].hours = roundHours(stats[type].hours + intervention.hours);
      stats[type].dates.push(intervention.date);

      stats.total.interventions++;
      stats.total.hours = roundHours(stats.total.hours + intervention.hours);
    });

    // Calculer la moyenne hebdomadaire
    if (data.interventions.length > 0) {
      const periodDays = Math.ceil(
        (data.interventions[data.interventions.length - 1].date - data.interventions[0].date) / (1000 * 60 * 60 * 24)
      );
      const periodWeeks = Math.max(1, Math.ceil(periodDays / 7));
      stats.averageHoursPerWeek = Math.round((stats.total.hours / periodWeeks) * 10) / 10;
    } else {
      stats.averageHoursPerWeek = 0;
    }

    result[employeeId] = stats;
  });

  return result;
}

/**
 * Convertit le planning en lignes de devis
 * Génère UNE LIGNE PAR TYPE DE MAJORATION pour chaque employé
 *
 * @param {Object} interventions - Résultat de calculateTotalInterventions
 * @param {Object} contratParams - Paramètres tarifaires (non utilisé ici, conservé pour compatibilité)
 * @returns {Array<Object>} Lignes de prestations pour le devis
 */
export function convertToDevisLines(interventions, contratParams = {}) {
  const lines = [];
  let lineId = 1;

  Object.entries(interventions).forEach(([employeeId, stats]) => {
    // Ligne pour heures normales (sans majoration)
    if (stats.none.interventions > 0) {
      lines.push({
        id: lineId++,
        typePrestation: 'aide_domicile',
        quantite: roundHours(stats.none.hours / stats.none.interventions), // Heures par intervention
        nbInterventions: stats.none.interventions,
        employeeId,
        employeeName: stats.employeeName,
        majoration: 'none',
        distanceAllerKm: stats.distanceAllerKm,
        details: {
          type: 'none',
          totalHours: stats.none.hours,
          dates: stats.none.dates
        }
      });
    }

    // Ligne pour heures dimanche (+25%)
    if (stats.weekend.interventions > 0) {
      lines.push({
        id: lineId++,
        typePrestation: 'aide_domicile',
        quantite: roundHours(stats.weekend.hours / stats.weekend.interventions),
        nbInterventions: stats.weekend.interventions,
        employeeId,
        employeeName: stats.employeeName,
        majoration: 'weekend',
        distanceAllerKm: stats.distanceAllerKm,
        details: {
          type: 'weekend',
          totalHours: stats.weekend.hours,
          dates: stats.weekend.dates
        }
      });
    }

    // Ligne pour heures jours fériés (+50%)
    if (stats.holiday.interventions > 0) {
      lines.push({
        id: lineId++,
        typePrestation: 'aide_domicile',
        quantite: roundHours(stats.holiday.hours / stats.holiday.interventions),
        nbInterventions: stats.holiday.interventions,
        employeeId,
        employeeName: stats.employeeName,
        majoration: 'holiday',
        distanceAllerKm: stats.distanceAllerKm,
        details: {
          type: 'holiday',
          totalHours: stats.holiday.hours,
          dates: stats.holiday.dates
        }
      });
    }

    // Ligne pour 1er mai (+100%)
    if (stats.may1.interventions > 0) {
      lines.push({
        id: lineId++,
        typePrestation: 'aide_domicile',
        quantite: roundHours(stats.may1.hours / stats.may1.interventions),
        nbInterventions: stats.may1.interventions,
        employeeId,
        employeeName: stats.employeeName,
        majoration: 'may1',
        distanceAllerKm: stats.distanceAllerKm,
        details: {
          type: 'may1',
          totalHours: stats.may1.hours,
          dates: stats.may1.dates
        }
      });
    }
  });

  console.log(`📋 ${lines.length} lignes de devis générées pour ${Object.keys(interventions).length} intervenant(s)`);

  return lines;
}

/**
 * Valide la cohérence du planning
 *
 * @param {Array<Object>} employeeSchedules - Configurations des employés
 * @param {number} weeklyHoursTarget - Volume horaire hebdomadaire contractuel
 * @returns {Object} { valid: boolean, errors: [], warnings: [] }
 */
export function validateSchedule(employeeSchedules, weeklyHoursTarget) {
  const errors = [];
  const warnings = [];

  if (!employeeSchedules || employeeSchedules.length === 0) {
    errors.push('Aucun intervenant configuré');
    return { valid: false, errors, warnings };
  }

  // Calculer le total hebdomadaire moyen
  let totalWeeklyHours = 0;
  let fixedHoursTotal = 0;
  let alternatingHoursTotal = 0;
  const daysUsageCount = {}; // Pour détecter les chevauchements

  employeeSchedules.forEach((empSchedule, index) => {
    const empName = empSchedule.employeeName || `Intervenant ${index + 1}`;

    // Calculer les heures par semaine pour cet employé
    let empWeeklyHours = 0;
    let empFixedHours = 0;

    // Jours fixes
    if (empSchedule.fixedDays) {
      empSchedule.fixedDays.forEach((dayConfig) => {
        const day = dayConfig.day;
        // Support ancien format (hours) et nouveau format (totalHours/totalMinutes/passagesPerDay)
        const totalHours = dayConfig.totalHours !== undefined ? dayConfig.totalHours : (dayConfig.hours || 0);
        const totalMinutes = dayConfig.totalMinutes || 0;
        const passagesPerDay = dayConfig.passagesPerDay || 1;

        const totalTimeHours = totalHours + (totalMinutes / 60);

        // Validation: durée totale > 0
        if (totalTimeHours <= 0) {
          errors.push(`${empName} : Durée invalide pour ${['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'][day]} (doit être > 0)`);
        }

        // Validation: passages ≥ 1
        if (passagesPerDay < 1) {
          errors.push(`${empName} : Nombre de passages invalide pour ${['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'][day]} (doit être ≥ 1)`);
        }

        // Validation: durée par passage ≥ 15 minutes (0.25h)
        const hoursPerPassage = totalTimeHours / passagesPerDay;
        if (hoursPerPassage < 0.25) {
          warnings.push(`${empName} : Durée par passage très courte (${Math.round(hoursPerPassage * 60)}min) pour ${['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'][day]}`);
        }

        empWeeklyHours += totalTimeHours;
        empFixedHours += totalTimeHours;

        // Tracker l'utilisation des jours
        if (!daysUsageCount[day]) daysUsageCount[day] = {};
        if (!daysUsageCount[day].fixed) daysUsageCount[day].fixed = [];
        daysUsageCount[day].fixed.push(empName);
      });
    }

    // Alternance A/B
    if (empSchedule.alternatingPattern && empSchedule.alternatingPattern.enabled) {
      const { weekA, weekB } = empSchedule.alternatingPattern;

      // Moyenne sur 2 semaines
      let alternatingHours = 0;

      if (weekA) {
        weekA.forEach((dayConfig) => {
          const day = dayConfig.day;
          // Support ancien format (hours) et nouveau format (totalHours/totalMinutes/passagesPerDay)
          const totalHours = dayConfig.totalHours !== undefined ? dayConfig.totalHours : (dayConfig.hours || 0);
          const totalMinutes = dayConfig.totalMinutes || 0;
          const passagesPerDay = dayConfig.passagesPerDay || 1;

          const totalTimeHours = totalHours + (totalMinutes / 60);

          // Validation: durée totale > 0
          if (totalTimeHours <= 0) {
            errors.push(`${empName} : Durée invalide pour ${['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'][day]} (semaine A, doit être > 0)`);
          }

          // Validation: passages ≥ 1
          if (passagesPerDay < 1) {
            errors.push(`${empName} : Nombre de passages invalide pour ${['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'][day]} (semaine A, doit être ≥ 1)`);
          }

          // Validation: durée par passage ≥ 15 minutes (0.25h)
          const hoursPerPassage = totalTimeHours / passagesPerDay;
          if (hoursPerPassage < 0.25) {
            warnings.push(`${empName} : Durée par passage très courte (${Math.round(hoursPerPassage * 60)}min) pour ${['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'][day]} (semaine A)`);
          }

          alternatingHours += totalTimeHours;

          if (!daysUsageCount[day]) daysUsageCount[day] = {};
          if (!daysUsageCount[day].weekA) daysUsageCount[day].weekA = [];
          daysUsageCount[day].weekA.push(empName);
        });
      }

      if (weekB) {
        weekB.forEach((dayConfig) => {
          const day = dayConfig.day;
          // Support ancien format (hours) et nouveau format (totalHours/totalMinutes/passagesPerDay)
          const totalHours = dayConfig.totalHours !== undefined ? dayConfig.totalHours : (dayConfig.hours || 0);
          const totalMinutes = dayConfig.totalMinutes || 0;
          const passagesPerDay = dayConfig.passagesPerDay || 1;

          const totalTimeHours = totalHours + (totalMinutes / 60);

          // Validation: durée totale > 0
          if (totalTimeHours <= 0) {
            errors.push(`${empName} : Durée invalide pour ${['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'][day]} (semaine B, doit être > 0)`);
          }

          // Validation: passages ≥ 1
          if (passagesPerDay < 1) {
            errors.push(`${empName} : Nombre de passages invalide pour ${['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'][day]} (semaine B, doit être ≥ 1)`);
          }

          // Validation: durée par passage ≥ 15 minutes (0.25h)
          const hoursPerPassage = totalTimeHours / passagesPerDay;
          if (hoursPerPassage < 0.25) {
            warnings.push(`${empName} : Durée par passage très courte (${Math.round(hoursPerPassage * 60)}min) pour ${['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'][day]} (semaine B)`);
          }

          alternatingHours += totalTimeHours;

          if (!daysUsageCount[day]) daysUsageCount[day] = {};
          if (!daysUsageCount[day].weekB) daysUsageCount[day].weekB = [];
          daysUsageCount[day].weekB.push(empName);
        });
      }

      // Pour l'alternance A/B : on calcule la moyenne sur 2 semaines
      // Car semaine A et semaine B alternent, donc sur une semaine donnée on ne fait que l'une ou l'autre
      const alternatingAverage = alternatingHours / 2;
      empWeeklyHours += alternatingAverage;
      alternatingHoursTotal += alternatingAverage;
    }

    fixedHoursTotal += empFixedHours;
    totalWeeklyHours += empWeeklyHours;

    // Vérifier si l'employé a au moins un jour configuré
    if (empWeeklyHours === 0) {
      warnings.push(`${empName} : Aucune intervention configurée`);
    }
  });

  // Vérifier les chevauchements
  Object.entries(daysUsageCount).forEach(([day, usage]) => {
    const dayNames = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
    const dayName = dayNames[parseInt(day)];

    // Jours fixes avec plusieurs intervenants
    if (usage.fixed && usage.fixed.length > 1) {
      errors.push(
        `Chevauchement ${dayName} (fixe) : ${usage.fixed.join(', ')}`
      );
    }

    // Semaine A avec plusieurs intervenants
    if (usage.weekA && usage.weekA.length > 1) {
      errors.push(
        `Chevauchement ${dayName} (semaine A) : ${usage.weekA.join(', ')}`
      );
    }

    // Semaine B avec plusieurs intervenants
    if (usage.weekB && usage.weekB.length > 1) {
      errors.push(
        `Chevauchement ${dayName} (semaine B) : ${usage.weekB.join(', ')}`
      );
    }

    // Conflit entre fixe et alternance
    if (usage.fixed && (usage.weekA || usage.weekB)) {
      warnings.push(
        `Conflit potentiel ${dayName} : jour fixe et alternance configurés`
      );
    }
  });

  // Vérifier la concordance avec le volume contractuel
  const difference = Math.abs(totalWeeklyHours - weeklyHoursTarget);
  if (difference > 0.5) {
    if (totalWeeklyHours < weeklyHoursTarget) {
      warnings.push(
        `Volume configuré (${totalWeeklyHours}h/sem) inférieur au contrat (${weeklyHoursTarget}h/sem)`
      );
    } else {
      warnings.push(
        `Volume configuré (${totalWeeklyHours}h/sem) supérieur au contrat (${weeklyHoursTarget}h/sem)`
      );
    }
  }

  // Créer un détail explicatif du calcul
  let weeklyHoursDetail = '';
  if (fixedHoursTotal > 0 && alternatingHoursTotal > 0) {
    weeklyHoursDetail = `Jours fixes: ${fixedHoursTotal.toFixed(1)}h + Alternance A/B (moyenne): ${alternatingHoursTotal.toFixed(1)}h`;
  } else if (fixedHoursTotal > 0) {
    weeklyHoursDetail = `Jours fixes uniquement: ${fixedHoursTotal.toFixed(1)}h`;
  } else if (alternatingHoursTotal > 0) {
    weeklyHoursDetail = `Alternance A/B (moyenne sur 2 semaines): ${alternatingHoursTotal.toFixed(1)}h`;
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
    totalWeeklyHours,
    weeklyHoursDetail
  };
}

/**
 * Calcule un aperçu des coûts prévisionnels
 * (estimation basée sur les paramètres tarifaires)
 *
 * @param {Object} interventions - Résultat de calculateTotalInterventions
 * @param {Object} contratParams - Paramètres tarifaires
 * @returns {Object} Détails des coûts estimés
 */
export function calculateEstimatedCosts(interventions, contratParams) {
  // Cette fonction sera complétée lors de l'intégration
  // Pour l'instant, retour d'un objet basique
  return {
    message: 'Calcul des coûts disponible après génération des lignes de devis',
    totalInterventions: Object.values(interventions).reduce((sum, emp) => sum + emp.total.interventions, 0),
    totalHours: Object.values(interventions).reduce((sum, emp) => sum + emp.total.hours, 0)
  };
}

export default {
  getMajorationTypeForDate,
  generateInterventionSchedule,
  calculateTotalInterventions,
  convertToDevisLines,
  validateSchedule,
  calculateEstimatedCosts
};
